import { Button } from '@p2p/components';
import { useLazyCheckDisputeQuery, usePostDisputeMutation } from '@p2p/services';
import {
  getFirstErrorMessage,
  isSpecificServerError,
  isUUID,
  setFavicon,
} from '@p2p/shared/functions';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { DisputeConfirmModal, DisputeFileUpload, DisputeInput } from './components';
import styles from './DisputePayment.module.scss';
import type { DisputeFormValues } from './DisputePayment.types';

const DisputePayment = () => {
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [checkDispute, { data: disputeData, isFetching: isCheckDisputeFetching }] =
    useLazyCheckDisputeQuery();
  const status = disputeData?.order_status;
  const sumOrder = disputeData?.real_income;
  const [createDispute] = usePostDisputeMutation();
  const { watch, register, setValue, getValues, reset } = useForm<DisputeFormValues>({
    defaultValues: {
      id: '',
      sum: undefined,
      comment: '',
      file1: null,
      file2: null,
    },
    mode: 'onChange',
  });
  const [id, sum, file1, file2] = watch(['id', 'sum', 'file1', 'file2']);
  const sumRegister = register('sum');
  const commentRegister = register('comment');

  const submitDispute = useCallback(async () => {
    const { id: rawId, sum: rawSum, comment: rawComment, file1, file2 } = getValues();

    if (!rawId || (!file1 && !file2)) {
      alert('Пожалуйста, заполните ID платежа и загрузите файл.');
      return;
    }
    const formData = new FormData();
    const normalizedId = rawId.trim();
    if (isUUID(normalizedId)) {
      formData.append('order_withdrawal_id', normalizedId);
    } else {
      formData.append('merchant_order_id', normalizedId);
    }

    formData.append('new_real_income', rawSum || '0');
    if (file1) {
      formData.append('files', file1);
    }
    if (file2) {
      formData.append('files', file2);
    }
    if (rawComment) {
      formData.append('comment', rawComment);
    }
    setModalActive(false);

    try {
      const response = await createDispute(formData).unwrap();
      alert(response?.detail);
    } catch (error) {
      if (isSpecificServerError(error, 'Диспут по этой заявке уже был открыт ранее')) {
        alert('Создание диспута отклонено. Диспут по данной операции уже открыт.');
      } else {
        if (error && typeof error === 'object' && 'data' in error) {
          const firstErrorMessage = getFirstErrorMessage(error.data);
          const errorMessage = firstErrorMessage
            ? firstErrorMessage
            : Array.isArray(error.data)
              ? error.data[0]
              : error.data;
          alert(firstErrorMessage || errorMessage);
        }
      }
    } finally {
      reset();
    }
  }, [createDispute, getValues, reset]);

  const handleCheckDispute = async () => {
    const { id: rawId, file1, file2 } = getValues();
    if (!rawId || (!file1 && !file2)) {
      alert('Пожалуйста, заполните ID платежа и загрузите файл.');
      return;
    }

    try {
      const response = await checkDispute(rawId.trim()).unwrap();
      if (response && ['AA', 'SF', 'FR', 'AAD', 'TO'].includes(response.order_status)) {
        setModalActive(true);
      } else {
        await submitDispute();
      }
    } catch (error) {
      console.log(error);
      alert('Произошла ошибка при проверке диспута.');
    }
  };

  useEffect(() => {
    setFavicon('/favicon_dispute.svg');
  }, []);

  return (
    <>
      <DisputeConfirmModal
        active={modalActive}
        setActive={setModalActive}
        status={status}
        sumOrder={sumOrder}
        sum={sum}
        sumRegister={sumRegister}
        onSubmit={submitDispute}
      />
      <div className={styles.blockDispute}>
        <div className={styles.disputeCard}>
          <div className={styles.disputeCardHead}>Оспорить платеж</div>
          <DisputeInput register={register('id')} title="ID платежа" placeholder="Введите ID" />
          <DisputeFileUpload
            label="Чек №1"
            file={file1}
            inputId="fileInput1"
            onSetFile={(file) => setValue('file1', file, { shouldDirty: true })}
          />
          <DisputeFileUpload
            label="Чек №2"
            file={file2}
            inputId="fileInput2"
            onSetFile={(file) => setValue('file2', file, { shouldDirty: true })}
          />
          <DisputeInput register={commentRegister} title="Комментарий" />
          <Button
            variant="v1"
            onClick={handleCheckDispute}
            disabled={!id || (!file1 && !file2) || isCheckDisputeFetching}
          >
            Оспорить
          </Button>
        </div>
      </div>
    </>
  );
};

export default DisputePayment;
